import React, { Fragment, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";

import BasicDetails from "./BasicDetails";
import CompanyAddress from "./CompanyAddress";
import { SNETButton, AlertBox, alertTypes } from "shared/dist/components";
import { organizationActions } from "../../../../Services/Redux/actionCreators";
import validator from "shared/dist/utils/validator";
import { orgOnboardingConstraints } from "./validationConstraints";
import { organizationSetupStatuses, organizationTypes } from "../../../../Utils/organizationSetup";
import { userEntities } from "../../../../Utils/user";
import ConfirmationPopup from "./ConfirmationPopup";
import { isUndefined } from "lodash";
import classes from "./Organization.module.css";
import { onboardingRoutes } from "../../Routes";
import { useNavigateTo } from "../../../../GlobalRouter/hook/NavigateTo";
import FormContentCard from "../../../../Components/PageFormComponents/FormContentCard";
import FormActions from "../../../../Components/PageFormComponents/FormActions";

const Organization = () => {
    const dispatch = useDispatch();
    const navigateTo = useNavigateTo();
    const user = useSelector((state) => state.user);
    const organization = useSelector((state) => state.organization);

    const [alert, setAlert] = useState({});
    const [allowDuns, setAllowDuns] = useState(false);
    const [showConfimationPopup, setShowConfimationPopup] = useState(false);
    const [isDisableFinishButton, setIsDisableFinishButton] = useState(false);

    useEffect(() => {
        setIsDisableFinishButton(isFormInvalid());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organization]);

    useEffect(() => {
        setAllowDuns(organization.uuid ? (organization.duns ? true : false) : true);
    }, [organization.duns, organization.uuid, setAllowDuns]);

    useEffect(() => {
        if (
            organization.state.state === organizationSetupStatuses.ONBOARDING_REJECTED &&
            !Boolean(alert.type)
        ) {
            setAlert({
                type: alertTypes.ERROR,
                message: "Your organization has been rejected.",
            });
        } else if (
            organization.state.state === organizationSetupStatuses.CHANGE_REQUESTED &&
            !Boolean(alert.type)
        ) {
            setAlert({
                type: alertTypes.ERROR,
                message: "Please validate the details provided and submit again for approval",
            });
        }
    }, [organization.state.state, setAlert, alert]);

    const isFormInvalid = () => {
        let orgValidationConstraints = orgOnboardingConstraints;
        let organizationForValidation = {
            name: organization?.name,
            website: organization?.website,
            phone: organization.contacts[0]?.phone,
        };

        if (user.entity !== userEntities.INDIVIDUAL) {
            organizationForValidation.id = organization?.id;
        } else {
            delete orgValidationConstraints.id;
        }
        if (allowDuns) {
            organizationForValidation.duns = organization?.duns;
        } else {
            delete orgValidationConstraints.duns;
        }

        const invalidFields = validator(organizationForValidation, orgValidationConstraints, {
            format: "flat",
        });
        return !isUndefined(invalidFields);
    };

    const handleNavigateBack = () => {
        navigateTo.onboarding(onboardingRoutes.SINGULARITY_ACCOUNT.path);
    };

    const handleFinish = () => {
        setShowConfimationPopup(true);
    };

    const handleClosePopup = () => {
        setShowConfimationPopup(false);
    };

    const handleContinue = async () => {
        setShowConfimationPopup(false);
        setAlert({});
        try {
            let orgUuid;
            const orgData = { ...organization, duns: allowDuns ? organization.duns : "" };
            if (user.entity === userEntities.INDIVIDUAL) {
                orgData.type = organizationTypes.INDIVIDUAL;
            }
            const data = await dispatch(
                organizationActions.createOrganization(orgData, user.email)
            );
            orgUuid = data.org_uuid;
            if (data.state.state === organizationSetupStatuses.ONBOARDING_APPROVED) {
                navigateTo.organizationSetup({ orgUuid });
            }
        } catch (error) {
            console.error("create org: ", error);
            return setAlert({
                type: alertTypes.ERROR,
                message: "Unable to finish organization authentication. Please try later",
            });
        }
    };

    const handleCancel = () => {
        dispatch(organizationActions.resetOrganizationData());
        navigateTo.onboarding(onboardingRoutes.SINGULARITY_ACCOUNT.path);
    };

    return (
        <Fragment>
            <FormContentCard title="Organization Verification Required">
                <Typography>
                    Fields marked with <span className={classes.isRequired}>*</span> are required
                </Typography>
                <Typography>
                    {user.entity === userEntities.INDIVIDUAL ||
                    organization.type === organizationTypes.INDIVIDUAL
                        ? `Please provide your company organization details and individual identity
                        for the verification process.`
                        : `Please provide your company organization details and your DUNS number
                        for the verification process.`}
                </Typography>
                <BasicDetails allowDuns={allowDuns} setAllowDuns={setAllowDuns} />
                <CompanyAddress />
                <div className={classes.alertBoxContainer}>
                    <AlertBox type={alert.type} message={alert.message} children={alert.children} />
                </div>
            </FormContentCard>
            <FormActions>
                <SNETButton color="primary" children="cancel" onClick={handleCancel} />
                <SNETButton color="primary" children="back" onClick={handleNavigateBack} />
                <SNETButton
                    color="primary"
                    variant="contained"
                    children="finish"
                    onClick={handleFinish}
                    disabled={
                        organization.state.state ===
                            organizationSetupStatuses.ONBOARDING_REJECTED || isDisableFinishButton
                    }
                />
            </FormActions>
            <ConfirmationPopup
                open={showConfimationPopup}
                handleClose={handleClosePopup}
                handleContinue={handleContinue}
            />
        </Fragment>
    );
};

export default Organization;
